import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  endpointFundPATCH,
  endpointFundPOST,
  FundPostPayload,
} from '~/api/def/endpoints/fundEndpoint';
import { endpointJurisdictionsGET } from '~/api/def/endpoints/jurisdictionEndpoint';
import { FormLabelInGrid } from '~/components/forms/FormLabelInGrid';
import { BqFormErrorText } from '~/components/ui/misc/bqTag/BqFormErrorText';
import { currencyList } from '~/config/currenciesStatic';
import { ClientCreateFundRequest } from '~/config/zodTypes/fund';
import { Fund } from '~/types';

export interface FieldType
  extends Omit<ClientCreateFundRequest, 'terminationDate' | 'inceptionDate'> {
  terminationDate: Date;
  inceptionDate: Date;
}

export const date2ApiString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Add 1 to month as it's zero-indexed
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
export type FundFormStatusEvt = {
  isError?: boolean;
  message?: string;
};
export type FundFormProps =
  | {
      initialValue?: undefined;
      fundId?: undefined;
      submitText?: string;
      formMode: 'create';
      onSubmitComplete?: (status?: FundFormStatusEvt) => void;
    }
  | {
      initialValue: Fund;
      fundId: string;
      submitText?: string;
      formMode: 'edit';
      onSubmitComplete?: (status?: FundFormStatusEvt) => void;
    };

type FundFormValues = Omit<Fund, 'size'> & { size: number | null };
const emptyFund: FundFormValues = {
  fundId: '',
  jurisdiction: '',
  name: '',
  size: null,
  currency: '',
  inceptionDate: null,
  terminationDate: null,
};

export function FundForm(props: FundFormProps) {
  const [jurisdictions, setJurisdictions] = React.useState<
    Array<{ label: string; value: string; id: string }> | undefined
  >();
  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!jurisdictions) {
      setIsJurisdictionsLoading(true);
      endpointJurisdictionsGET().then((res) => {
        if (res?.success && res.data) {
          setJurisdictions(
            res.data.map((j) => ({
              label: j.name,
              value: j.abbreviation,
              id: j.id,
            })),
          );
          setIsJurisdictionsLoading(false);
        }
      });
    }
  }, [jurisdictions]);

  const defaultValues: FundFormValues = props.initialValue
    ? props.initialValue
    : emptyFund;

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FundFormValues>({ defaultValues });

  const onSubmit = async (data: FundFormValues) => {
    setIsSubmitting(true);
    const selectedJurisdiction = jurisdictions?.find(
      (j) => j.value === data.jurisdiction,
    );
    if (!selectedJurisdiction?.id) throw new Error('Jurisdiction not found');
    const postPayload: Omit<FundPostPayload, 'id'> = {
      name: data.name,
      jurisdictionId: selectedJurisdiction.id,
      size: data.size ?? 0,
      currency: data.currency,
      inceptionDate: data.inceptionDate
        ? format(new Date(data.inceptionDate), 'yyyy-MM-dd')
        : null,
      terminationDate: data.terminationDate
        ? format(new Date(data.terminationDate), 'yyyy-MM-dd')
        : null,
    };
    const status: FundFormStatusEvt = { message: '', isError: true };
    if (props.formMode === 'create') {
      await endpointFundPOST(postPayload).then((res) => {
        status.isError = !res?.success;
        if (res?.success) {
          status.message = res?.success
            ? 'Fund:' + data.name + ' has been created'
            : 'Failed to create fund';
        }
      });
    } else {
      const patchPayload: FundPostPayload = {
        ...postPayload,
        id: props.fundId,
      };
      await endpointFundPATCH(props.fundId, patchPayload).then((res) => {
        status.isError = !res?.success;
        if (res?.success) {
          status.message = res?.success
            ? 'Fund:' + data.name + ' has been updated'
            : 'Failed to update fund';
        }
      });
    }
    setIsSubmitting(false);
    props?.onSubmitComplete && props.onSubmitComplete(status);
    reset();
  };
  const getFormErrorMessage = (name: keyof FundFormValues) => {
    return errors[name] ? (
      <BqFormErrorText error={errors[name].message} />
    ) : (
      <></>
    );
  };

  return (
    <div className="card flex justify-content-center">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-column gap-2"
      >
        <div className="flex-column flex h-full">
          <div className="grid">
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Fund Name - is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Fund Name"
                  />
                  <div className="col col-9 text-left">
                    <InputText
                      id={field.name}
                      className={classNames({
                        'p-invalid': fieldState.error,
                        'w-16rem': true,
                      })}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="jurisdiction"
              control={control}
              rules={{ required: 'Select Jurisdiction.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Jurisdiction"
                  />
                  <div className="col col-9 text-left">
                    <Dropdown
                      id="jurisdiction"
                      value={field.value}
                      className="w-16rem"
                      loading={isJurisdictionsLoading}
                      filter
                      options={jurisdictions}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />
            <FormLabelInGrid required htmlFor={'size'} label="Fund Size" />
            <div className="col col-9 text-left">
              <div className="p-inputgroup flex-1">
                <Controller
                  name="size"
                  control={control}
                  rules={{ required: 'Size is required.' }}
                  render={({ field }) => (
                    <InputNumber
                      id="size"
                      placeholder="size"
                      className="max-w-9rem"
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                    />
                  )}
                />

                <Controller
                  name="currency"
                  control={control}
                  rules={{ required: 'Selecting currency is required.' }}
                  render={({ field }) => (
                    <Dropdown
                      id={field.name}
                      value={field.value}
                      className="p-inputgroup-addon max-w-7rem"
                      filter
                      options={currencyList}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>
              {getFormErrorMessage('size')}
              {getFormErrorMessage('currency')}
            </div>
            <Controller
              name="inceptionDate"
              control={control}
              rules={{ required: 'Select inception date.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Inception Date"
                  />
                  <div className="col col-9 text-left">
                    <Calendar
                      id={field.name}
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(e) => {
                        field.onChange(e?.value ? date2ApiString(e.value) : '');
                      }}
                      showIcon
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />

            <Controller
              name="terminationDate"
              control={control}
              rules={{ required: 'Select termination date.' }}
              render={({ field, fieldState }) => (
                <>
                  <FormLabelInGrid
                    required
                    htmlFor={field.name}
                    label="Termination Date"
                  />
                  <div className="col col-9 text-left">
                    <Calendar
                      id={field.name}
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(e) => {
                        field.onChange(e?.value ? date2ApiString(e.value) : '');
                      }}
                      showIcon
                    />
                    <BqFormErrorText error={fieldState.error?.message} />
                  </div>
                </>
              )}
            />
            <div className="col col-3 text-right pt-3"></div>
            <div className="col col-9 text-left">
              <Button loading={isSubmitting} disabled={isSubmitting}>
                {props.formMode === 'create' ? 'Create Fund' : 'Save Fund'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
